import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE, api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Video, Clock, MessageSquare, ArrowLeft, Loader2, Users, Send, Radio, StopCircle } from "lucide-react";
import NpsModal from "@/components/NpsModal";

const NPS_MIN_DURATION_MS = 15 * 60 * 1000; // 15 min

/**
 * Fase 10 · Módulo B — Sala DCC con Jitsi Meet embed + chat persistente.
 *
 * Flujo:
 * 1. GET /public/live/{sid} → meta pública (título, hora, joinable)
 * 2. Si authenticated & joinable → GET /live/{sid}/config → jitsi_room_name + role
 * 3. Cargar external_api.js de meet.jit.si y montar iframe API
 * 4. POST /live/{sid}/attendance/join al montar; leave al desmontar
 * 5. Polling GET /live/{sid}/chat cada 5s + POST para enviar
 */
export default function Live() {
  const { session_id: sid } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [error, setError] = useState(null);
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const [jitsiLoading, setJitsiLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const joinedAtRef = useRef(null);
  const [showNps, setShowNps] = useState(false);
  const npsShownRef = useRef(false);
  const pendingNavRef = useRef(null);

  // ---- Meta (public) ----
  useEffect(() => {
    axios.get(`${API_BASE}/public/live/${sid}`)
      .then((r) => setMeta(r.data))
      .catch(() => setError("Sesión no encontrada"));
  }, [sid]);

  // ---- Countdown ----
  useEffect(() => {
    if (!meta) return;
    const tick = () => {
      const t = new Date(meta.starts_at).getTime() - Date.now();
      if (t <= 0) { setCountdown("En vivo"); return; }
      const h = Math.floor(t / 3600000);
      const m = Math.floor((t % 3600000) / 60000);
      const s = Math.floor((t % 60000) / 1000);
      setCountdown(`${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [meta]);

  // ---- Config (auth required, joinable window) ----
  useEffect(() => {
    if (!meta?.is_joinable_now || !user) return;
    (async () => {
      try {
        const r = await api.get(`/live/${sid}/config`);
        setConfig(r.data);
        setRecording(!!r.data.recording_started_at);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (typeof detail === "object" && detail.code === "session_outside_join_window") {
          setConfigError("La sala aún no está abierta.");
        } else {
          setConfigError(formatApiError(detail));
        }
      }
    })();
  }, [meta, user, sid]);

  // ---- Jitsi mount ----
  const mountJitsi = useCallback(() => {
    if (!config || !jitsiContainerRef.current) return;
    if (jitsiApiRef.current) return;
    setJitsiLoading(true);

    const loadScript = () => new Promise((resolve, reject) => {
      if (window.JitsiMeetExternalAPI) return resolve();
      const s = document.createElement("script");
      s.src = `https://${config.jitsi_domain}/external_api.js`;
      s.async = true;
      s.onload = resolve;
      s.onerror = () => reject(new Error("no_pudo_cargar_jitsi"));
      document.body.appendChild(s);
    });

    loadScript().then(() => {
      const options = {
        roomName: config.jitsi_room_name,
        parentNode: jitsiContainerRef.current,
        width: "100%", height: "100%",
        userInfo: { displayName: config.display_name, email: config.email || undefined },
        configOverwrite: {
          prejoinPageEnabled: false,
          disableDeepLinking: true,
          startWithAudioMuted: config.role !== "moderator",
          startWithVideoMuted: false,
          enableWelcomePage: false,
        },
        interfaceConfigOverwrite: {
          MOBILE_APP_PROMO: false,
          SHOW_JITSI_WATERMARK: false,
          SHOW_CHROME_EXTENSION_BANNER: false,
          DEFAULT_BACKGROUND: "#0B0F14",
          TOOLBAR_BUTTONS: [
            "microphone", "camera", "closedcaptions", "desktop", "fullscreen",
            "hangup", "chat", "raisehand", "videoquality", "filmstrip",
            "shortcuts", "tileview", "select-background", "toggle-camera", "settings",
          ],
        },
      };
      const api = new window.JitsiMeetExternalAPI(config.jitsi_domain, options);
      jitsiApiRef.current = api;
      api.addEventListener("videoConferenceJoined", () => {
        setJitsiLoading(false);
        joinedAtRef.current = Date.now();
      });
      const handleLeave = () => {
        try { api.dispose(); } catch {}
        jitsiApiRef.current = null;
        // NPS gate: only alumnos / attendees (not moderators) with >=15 min session
        const dur = joinedAtRef.current ? Date.now() - joinedAtRef.current : 0;
        const isModerator = config.role === "moderator";
        if (!isModerator && dur >= NPS_MIN_DURATION_MS && !npsShownRef.current) {
          npsShownRef.current = true;
          pendingNavRef.current = () => nav(-1);
          setShowNps(true);
        } else {
          nav(-1);
        }
      };
      api.addEventListener("videoConferenceLeft", handleLeave);
      api.addEventListener("readyToClose", () => {
        try { api.dispose(); } catch {}
        jitsiApiRef.current = null;
      });
    }).catch((e) => {
      setJitsiLoading(false);
      toast.error("No se pudo cargar Jitsi. Verifica tu conexión.");
    });
  }, [config, nav]);

  useEffect(() => { mountJitsi(); }, [mountJitsi]);

  // ---- Attendance ----
  useEffect(() => {
    if (!config) return;
    api.post(`/live/${sid}/attendance/join`).catch(() => {});
    const beforeUnload = () => {
      // best-effort leave — sendBeacon is fire-and-forget
      if (navigator.sendBeacon) {
        const token = localStorage.getItem("dcc.access") || "";
        const blob = new Blob([JSON.stringify({})], { type: "application/json" });
        // Beacon can't include auth headers; fallback to plain fetch keepalive:
        fetch(`${API_BASE}/live/${sid}/attendance/leave`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` },
          keepalive: true,
        }).catch(() => {});
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      api.post(`/live/${sid}/attendance/leave`).catch(() => {});
      if (jitsiApiRef.current) {
        try { jitsiApiRef.current.dispose(); } catch {}
        jitsiApiRef.current = null;
      }
    };
  }, [config, sid]);

  // ---- Recording (mocked) ----
  const toggleRecording = async () => {
    try {
      if (!recording) {
        const r = await api.post(`/instructor/sessions/${sid}/recording/start`);
        toast.warning(`MOCKED · ${r.data.message}`, { duration: 6000 });
        setRecording(true);
      } else {
        await api.post(`/instructor/sessions/${sid}/recording/stop`, {});
        toast.success("Grabación detenida. Sube el MP4 manualmente.");
        setRecording(false);
      }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (error) return <FullMessage title="Sesión no encontrada" body={error} />;
  if (!meta) return <FullMessage title="Cargando sesión…" />;

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white flex flex-col" data-testid="live-shell">
      <header className="border-b border-white/10 px-4 sm:px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => window.history.back()} className="text-gray-300 p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Logo size="sm" dark />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-[#F26A21] font-bold">Sala DCC</span>
      </header>

      <main className="flex-1 flex flex-col lg:flex-row min-h-0">
        <section className="flex-1 min-h-[420px] lg:min-h-0 relative" data-testid="live-video-area">
          {!meta.is_joinable_now ? (
            <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
              <div>
                <Video className="w-12 h-12 text-[#F26A21] mx-auto opacity-50" />
                <p className="mt-4 text-lg font-bold">Aún no comienza</p>
                <p className="mt-2 text-sm text-gray-400">Falta <span className="text-[#F26A21] font-mono font-bold" data-testid="live-countdown">{countdown}</span></p>
                <p className="mt-4 text-xs text-gray-500 max-w-md mx-auto">
                  La sala abre 15 minutos antes del inicio. Prepara tus audífonos y tu conexión.
                </p>
                {!user && (
                  <button onClick={() => nav("/login")} className="mt-4 px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm">
                    Iniciar sesión para entrar
                  </button>
                )}
              </div>
            </div>
          ) : configError ? (
            <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
              <div>
                <p className="text-sm text-red-300">{configError}</p>
              </div>
            </div>
          ) : !config ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Conectando…</p>
            </div>
          ) : (
            <>
              <div ref={jitsiContainerRef} className="absolute inset-0" data-testid="live-jitsi-container" />
              {jitsiLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0B0B0B]/80 pointer-events-none">
                  <p className="text-sm text-gray-300 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando Jitsi Meet…</p>
                </div>
              )}
              {config.role === "moderator" && (
                <button onClick={toggleRecording}
                        data-testid="live-recording-toggle"
                        className={`absolute top-3 right-3 z-10 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${recording ? "bg-red-500 text-white" : "bg-white/10 text-white border border-white/20"}`}>
                  {recording ? <><StopCircle className="w-3 h-3" /> Detener grabación</> : <><Radio className="w-3 h-3" /> Grabar (MOCKED)</>}
                </button>
              )}
            </>
          )}
        </section>

        {/* Sidebar: title + chat + attendance (visible only inside window) */}
        <aside className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-white/10 bg-[#111827] flex flex-col min-h-[400px] lg:min-h-0">
          <div className="px-4 py-3 border-b border-white/10 flex-shrink-0">
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">{meta.platform === "dcc_room" ? "Jitsi Meet · DCC" : meta.platform}</p>
            <h1 className="mt-1 text-base font-bold" data-testid="live-title">{meta.title}</h1>
            <p className="mt-1 text-xs text-gray-400 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {new Date(meta.starts_at).toLocaleString("es-MX")} · {meta.duration_min} min
            </p>
          </div>

          {meta.is_joinable_now && config ? (
            <>
              <LiveChat sid={sid} user={user} isModerator={config.role === "moderator"} />
              <AttendancePanel sid={sid} isModerator={config.role === "moderator"} />
            </>
          ) : (
            <div className="p-4 text-xs text-gray-500 flex-1 flex items-center justify-center text-center">
              El chat DCC y la asistencia se activan cuando la sala está abierta.
            </div>
          )}
        </aside>
      </main>
      {showNps && (
        <NpsModal
          sessionId={sid}
          sessionTitle={meta?.title}
          onDone={() => {
            setShowNps(false);
            const cb = pendingNavRef.current; pendingNavRef.current = null;
            if (cb) cb(); else nav(-1);
          }}
          onClose={() => {
            setShowNps(false);
            const cb = pendingNavRef.current; pendingNavRef.current = null;
            if (cb) cb(); else nav(-1);
          }}
        />
      )}
    </div>
  );
}

function LiveChat({ sid, user, isModerator }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const lastCursorRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await api.get(`/live/${sid}/chat`, {
          params: lastCursorRef.current ? { since: lastCursorRef.current } : {},
        });
        if (stop) return;
        if (r.data.items?.length) {
          setMsgs((p) => [...p, ...r.data.items]);
          lastCursorRef.current = r.data.items[r.data.items.length - 1].created_at;
          setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
        }
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 5000);
    return () => { stop = true; clearInterval(id); };
  }, [sid]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/live/${sid}/chat`, { body: input.trim() });
      setMsgs((p) => [...p, r.data]);
      lastCursorRef.current = r.data.created_at;
      setInput("");
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 border-b border-white/10" data-testid="live-chat">
      <div className="px-3 pt-3 pb-2 border-b border-white/10">
        <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold inline-flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Chat DCC · persistente
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {msgs.length === 0 && <p className="text-xs text-gray-500 text-center py-8">Aún no hay mensajes en esta sala.</p>}
        {msgs.map((m) => {
          const mine = m.from_user_id === user?.id;
          const mod = m.role === "moderator";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 ${mine ? "bg-[#F26A21] text-[#0B0F14]" : "bg-white/10 text-white"}`}>
                {!mine && (
                  <p className={`text-[10px] uppercase tracking-widest font-bold ${mod ? "text-amber-300" : "text-gray-400"}`}>
                    {m.from_user_name}{mod && " · mod"}
                  </p>
                )}
                <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                <p className={`mt-0.5 text-[10px] ${mine ? "text-[#0B0F14]/60" : "text-gray-500"}`}>
                  {new Date(m.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2" data-testid="live-chat-form">
        <input value={input} onChange={(e) => setInput(e.target.value)}
               placeholder="Escribe en el chat DCC…"
               data-testid="live-chat-input"
               className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
        <button type="submit" disabled={sending || !input.trim()}
                data-testid="live-chat-send"
                className="inline-flex items-center gap-1 rounded-md bg-[#F26A21] px-3 text-[#0B0F14] font-semibold text-sm disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}

function AttendancePanel({ sid, isModerator }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let stop = false;
    const load = () => api.get(`/live/${sid}/attendance`).then((r) => {
      if (!stop) setRows(r.data.items || []);
    }).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => { stop = true; clearInterval(id); };
  }, [sid]);
  const live = rows.filter((r) => !r.leave_at);
  return (
    <div className="px-3 py-3 flex-shrink-0" data-testid="live-attendance">
      <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold inline-flex items-center gap-1">
        <Users className="w-3 h-3" /> Asistencia · {live.length} en línea
      </p>
      <div className="mt-1 flex flex-wrap gap-1">
        {live.slice(0, 12).map((r) => (
          <span key={r.id} className={`text-[10px] px-2 py-0.5 rounded-full ${r.role === "moderator" ? "bg-amber-500/20 text-amber-200" : "bg-white/5 text-gray-300"}`}>
            {r.user_name || "—"}{r.role === "moderator" && " · mod"}
          </span>
        ))}
        {live.length > 12 && <span className="text-[10px] text-gray-400">+{live.length - 12}</span>}
      </div>
      {isModerator && rows.length > live.length && (
        <details className="mt-2 text-[11px] text-gray-400">
          <summary className="cursor-pointer">Ver historial ({rows.length - live.length} desconectados)</summary>
          <ul className="mt-1 space-y-0.5">
            {rows.filter((r) => r.leave_at).map((r) => (
              <li key={r.id}>{r.user_name} · {new Date(r.join_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })} → {new Date(r.leave_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function FullMessage({ title, body }) {
  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white flex items-center justify-center p-4 text-center">
      <div>
        <h1 className="text-2xl font-bold">{title}</h1>
        {body && <p className="mt-2 text-sm text-gray-400">{body}</p>}
      </div>
    </div>
  );
}
